/**
 * Shared constants, formatting utils, and data-fetching hook for the
 * invoice list tabs (My Invoices, Drafts, Rejected).
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { invoiceApi } from "../InvoiceApi";

// ─── Constants ────────────────────────────────────────────────────────────────

export const statusBadgeClass = {
  DRAFT:      "bg-orange-200 dark:bg-orange-700/60 text-gray-800 dark:text-gray-200",
  RECALLED:   "bg-neutral-200 dark:bg-neutral-700/40 text-gray-800 dark:text-gray-200",
  PROCESSING: "bg-blue-100 dark:bg-blue-900/40 text-black dark:text-blue-200",
  APPROVED:   "bg-green-100 dark:bg-green-900/40 text-black dark:text-green-200",
  REJECTED:   "bg-red-300 dark:bg-red-900/40 text-black dark:text-red-200",
  PENDING:    "bg-amber-100 dark:bg-amber-900/40 text-black dark:text-amber-200",
  DEFAULT:    "bg-gray-100 dark:bg-gray-700/40 text-black dark:text-gray-200",
};

export const auditBadgeClass = {
  PASSED:     "bg-emerald-100 dark:bg-emerald-900/40 text-black dark:text-emerald-200",
  FAILED:     "bg-red-100 dark:bg-red-900/40 text-black dark:text-red-200",
  PENDING:    "bg-yellow-100 dark:bg-yellow-900/40 text-black dark:text-yellow-200",
  DEFAULT:    "bg-gray-100 dark:bg-gray-700/40 text-black dark:text-gray-200",
};

export const paymentBadgeClass = {
  UNPAID:           "bg-red-100 dark:bg-red-900/40 text-black dark:text-red-200",
  "PARTIALLY PAID": "bg-yellow-100 dark:bg-yellow-900/40 text-black dark:text-yellow-200",
  PAID:             "bg-green-100 dark:bg-green-900/40 text-black dark:text-green-200",
  OVERPAID:         "bg-purple-100 dark:bg-purple-900/40 text-black dark:text-purple-200",
  REFUNDED:         "bg-blue-100 dark:bg-blue-900/40 text-black dark:text-blue-200",
  DEFAULT:          "bg-gray-100 dark:bg-gray-700/40 text-black dark:text-gray-200",
};

export const statusCls = (v) => statusBadgeClass[(v || "").toUpperCase()] || statusBadgeClass.DEFAULT;
export const auditCls  = (v) => auditBadgeClass[(v || "").toUpperCase()]  || auditBadgeClass.DEFAULT;
export const paymentCls= (v) => paymentBadgeClass[(v || "UNPAID").toUpperCase()] || paymentBadgeClass.DEFAULT;

/** Default status quick-filter when no saved view is active (matches invoiceSlice/InvoiceDetails). */
export const DEFAULT_QUICK_FILTER = "Pending";

// ─── Utils ────────────────────────────────────────────────────────────────────

export const formatMoney = (amount, currency) => {
  if (amount == null || amount === "") return null;
  const n = Number(amount);
  if (Number.isNaN(n)) return null;
  const safe = typeof currency === "string" ? currency.trim().toUpperCase() : "";
  const code = /^[A-Z]{3}$/.test(safe) ? safe : "USD";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${code}`;
  }
};

// ─── Hook: useInvoiceListData ─────────────────────────────────────────────────

/**
 * Fetches a page of invoices with AbortController cancellation, a
 * 250ms-delayed loading overlay, and visible error surfacing.
 *
 * @param {string}   invoiceStatus - backend invoiceStatus filter value ("" / null for all)
 * @param {string}   searchQuery   - free-text query
 * @param {number}   page          - 1-based page number
 * @param {number}   size          - page size
 * @param {*}        refreshKey    - any value; changing it forces a re-fetch
 * @param {function} [sortRows]    - optional (content) => sortedContent
 * @param {function} [onPageOutOfRange] - called with 1 when the current page is
 *   beyond the fetched totalPages; caller should use it to reset its page state.
 * @param {object}   [filter]      - nested AND/OR advanced-filter FilterNode tree (null/absent
 *   preserves the plain query+status search)
 */
export const useInvoiceListData = ({
  invoiceStatus,
  searchQuery = "",
  page,
  size,
  refreshKey,
  sortRows,
  sortBy = "invoiceDate",
  ascending = false,
  onPageOutOfRange,
  filter,
}) => {
  const [invoices,          setInvoices]          = useState([]);
  const [totalElements,     setTotalElements]     = useState(0);
  const [totalPages,        setTotalPages]        = useState(1);
  const [isLoading,         setIsLoading]         = useState(false);
  const [showLoadingOverlay,setShowLoadingOverlay]= useState(false);
  const [errorMessage,      setErrorMessage]      = useState("");

  const sortRowsRef = useRef(sortRows);
  sortRowsRef.current = sortRows;

  const onPageOutOfRangeRef = useRef(onPageOutOfRange);
  onPageOutOfRangeRef.current = onPageOutOfRange;

  // ── Loading overlay: only show after 250ms to avoid flash on fast requests ──
  useEffect(() => {
    if (!isLoading) { setShowLoadingOverlay(false); return; }
    const t = setTimeout(() => setShowLoadingOverlay(true), 250);
    return () => clearTimeout(t);
  }, [isLoading]);

  const fetchInvoices = useCallback(async (signal) => {
    try {
      setIsLoading(true);
      setErrorMessage("");

      const resp = await invoiceApi.detailedSearch(
        {
          query: searchQuery,
          invoiceStatus: invoiceStatus || "",
          // The backend NPEs building its Sort object if sortBy is missing/null —
          // always send a non-null value even though these tabs re-sort client-side.
          sortBy: sortBy ?? "invoiceDate",
          ascending,
          ...(filter ? { filter } : {}),
        },
        page - 1,
        size,
        signal
      );

      if (signal?.aborted) return;

      const content = Array.isArray(resp?.content) ? resp.content : [];
      const fetchedTotalPages = resp?.totalPages ?? 1;
      setInvoices(sortRowsRef.current ? sortRowsRef.current(content) : content);
      setTotalElements(resp?.totalElements ?? content.length);
      setTotalPages(fetchedTotalPages);

      // Current page no longer exists in this result set — snap back to page 1.
      if (page > 1 && page > fetchedTotalPages) {
        onPageOutOfRangeRef.current?.(1);
      }
    } catch (err) {
      if (signal?.aborted || err?.code === "ERR_CANCELED") return;
      console.error("Error fetching invoices:", err);
      setInvoices([]);
      setTotalElements(0);
      setTotalPages(1);
      setErrorMessage(
        err?.response?.data?.content ||
        err?.response?.data?.message ||
        "Could not load invoices. Please try again."
      );
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  }, [invoiceStatus, searchQuery, page, size, sortBy, ascending, filter]);

  useEffect(() => {
    const controller = new AbortController();
    fetchInvoices(controller.signal);
    return () => controller.abort();
  }, [fetchInvoices, refreshKey]);

  return {
    invoices,
    totalElements,
    totalPages,
    isLoading,
    showLoadingOverlay,
    errorMessage,
    refetch: fetchInvoices,
  };
};
